<yc-modal *ngIf="formGroup && !hide"
  [formGroup]="formGroup"
  [modalHeader]="'common:hdrSelectYourOrganization' | ycI18n: 'Select Your Organization'"
  [helpLink]="!isManager ? searchAllOrganizationsHelpLinkApplicant : searchAllOrganizationsHelpLinkManager">

  <!-- Favorite Orgs Selector -->
  <div *ngIf="isFavoriteOrgsView"
    role="grid">
    <div class="mb-4 font-875-rem font-550">
      {{ favoriteOrgsHelpText }}
    </div>
    <div role="rowgroup">
      <div #favOrgSearchResults *ngFor="let row of favoritesOrgSearchRepo.currentSet;"
        class="org-block"
        role="row"
        [tabIndex]="0"
        [attr.aria-label]="row.name"
        [attr.aria-selected]="selectedOrg?.id === row.id"
        [class.org-selected]="selectedOrg?.id === row.id"
        (click)="selectedOrg = row;"
        (keyup.enter)="selectedOrg = row;">
        <yc-nonprofit-block-table
          [name]="row.name"
          [id]="row.id" 
          [addressString]="row.address"
          [registrationId]="row.registrationId"
          [eligibleForGivingStatusId]="processorType === ProcessingTypes.YourCause ?
            row.eligibleForGivingStatusId :
            null"
          [icon]="row.iconURL || 'assets/img/nonprofit/icons/cat_10.gif'">
        </yc-nonprofit-block-table>
      </div>
    </div>
  </div>

  <!-- Organization Search View -->
  <div *ngIf="!isFavoriteOrgsView" 
    [formGroup]="formGroup">

    <!-- Search Filters -->
    <div class="d-flex justify-content-between">
      <!-- Search Term -->
      <yc-input 
        class="flex-grow-1"
        name="term"
        [showClear]="true"
        (keyup)="kickOffSearch()"
        [autofocus]="true"
        [placeholder]="'search:textSearchByNameOrRegistrationID' | ycI18n: 'Search by name or Registration ID'"
        [help]="regIdHelpText">
      </yc-input>
    </div>

    <!-- Filter By Location (Standard) -->
    <div *ngIf="!allowedOrgLocations || allowedOrgLocations.length === 0"
      class="d-flex align-items-center mt-2">
      <!-- Country Select -->
      <div class="me-2 flex-basis-50 me-2">
        <yc-typeahead-select
          [label]="'GLOBAL:COUNTRY' | ycI18n: 'Country'"
          [srOnlyLabel]="true"
          [items]="countrySelects"
          [disabled]="!hasInternational"
          name="countryCode"
          [showClear]="false"
          (onChange)="setRegionsOnCountryChange()">
        </yc-typeahead-select>
      </div>
      <!-- State Select -->
      <div [ycHidden]="regionSelects.length === 0"
        class="flex-basis-50">
        <yc-typeahead-select 
          name="stateProvRegCode"
          [items]="regionSelects"
          [showClear]="false"
          (onChange)="kickOffSearch()">
        </yc-typeahead-select>
      </div>
    </div>

    <!-- Filter by Specified Locations -->
    <div *ngIf="allowedOrgLocations?.length > 0"
      class="mt-2">
      <gc-location-filtering
        [allowedLocations]="allowedOrgLocations">
      </gc-location-filtering>
      <div>
        <yc-i18n
          key="common:textLocationFilterHelp"
          defaultValue="This program is only available to specific locations. Only organizations in the selected regions appear in the search.">
        </yc-i18n>
      </div>
    </div>

    <div *ngIf="showTabs"
      class="mt-3">
      <yc-horizontal-nav-tabs
        [tabs]="tabs"
        [useRoutes]="false"
        [bottomBorderOnNav]="true"
        [fullWidth]="true"
        (activeTabChanged)="onTabChange($event)">
      </yc-horizontal-nav-tabs>
    </div>

    <div>  
      <!-- No Results -->
      <div *ngIf="showNoResults"
        [ycBlockUi]="loading"
        class="no-results">

        <!-- Initial Message -->
        <div *ngIf="!hasStartedSearching"
          class="italic">
          <yc-i18n
            key="common:textOrgSearchInitialView3"
            defaultValue="Explore the nonprofit organizations registered with NPOconnect by searching for its name or Registration ID. For search tips, see our Help Center.">
          </yc-i18n>
          <yc-button
            [icon]="true"
            [tooltipText]="'common:textOpenHelpCenter' | ycI18n: 'Open help center'"
            [fontAwesomeIcon]="'arrow-up-right-from-square'"
            (onClick)="openHelpCenter()">
          </yc-button>
        </div>

        <!-- Charity Bucket Guidelines -->
        <div *ngIf="!!orgSearchGuidelines && useBucketSearch"
          class="mb-2 mt-4">
          <section class="mb-4">
            <div class="font-550 font-875-rem">
              <yc-i18n 
                [key]="hasStartedSearching ?
                  'search:textStillCantFindOrgProgramGuidelines' :
                  'PROGRAM:lblOrgSearchGuidelines'" 
                [defaultValue]="hasStartedSearching ?
                  'Still can\'t find your organization? It may have been removed as part of the program guidelines' :
                  'Organization Search Guidelines'">
              </yc-i18n>:
            </div>
            <div [innerHTML]="orgSearchGuidelines"></div>
          </section>
        </div>

        <!-- No Results Message-->
        <div *ngIf="hasStartedSearching"
          class="italic">
          <yc-i18n
            key="common:textNoOrgsCouldBeFound"
            defaultValue="No organizations could be found">
          </yc-i18n>
          <div class="mt-3">
            {{ selectedSearchType === SearchTabs.Client ?
              orgClientSearchNoResultsText :
              orgSearchNoResultsText }}
          </div>
        </div>
      </div>

      <!-- Search Results -->
      <div class="mt-4"
        role="region">
        <div role="grid">
          <div *ngIf="currentRepo &&
            currentRepo.currentSet.length > 0"
            role="rowgroup"
            [ycBlockUi]="loading">
            <div #orgSearchResults *ngFor="let row of currentRepo.currentSet;"
              class="org-block"
              role="row"
              [tabIndex]="0"
              [attr.aria-selected]="selectedOrg?.id === row.id"
              [attr.aria-label]="row.name"
              [class.org-selected]="selectedOrg?.id === row.id"
              (click)="selectedOrg = row;"
              (keyup.enter)="selectedOrg = row;">
              <yc-nonprofit-block-table 
                [name]="row.name"
                [id]="row.id"
                [parentName]="row.parentName"
                [parentId]="row.parentGuid"
                [parentRouterLink]="isManager && row.parentGuid ?
                  '/management/nonprofit/' + row.parentGuid + '/profile' : 
                  ''"
                [addressString]="row.address"
                [registrationId]="row.registrationId"
                [eligibleForGivingStatusId]="processorType === ProcessingTypes.YourCause ?
                  row.eligibleForGivingStatusId :
                  null"
                [icon]="{
                  iconURL: row.iconURL,
                  classification: row.classification
                } | getOrgIcon">
              </yc-nonprofit-block-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Pagination -->
  <yc-pagination
    [repository]="currentRepo | ycAs: $repo"
    [noBorderTop]="true"
    (pageChanged)="pageChanged = true">
  </yc-pagination>

  <!-- Modal Footer -->
  <div *ycModalFooter
    class="modal-footer justify-content-start flex-column align-items-start">
    <div class="d-flex align-items-center justify-content-between flex-grow-1 w-100">
      <!-- Add org -->
      <div *ngIf="showAddOrg || isFavoriteOrgsView">
        <yc-i18n
          key="common:textDontSeeTheOrganization"
          defaultValue="Don't see the organization?">
        </yc-i18n>
        <yc-button
          class="ms-2"
          [fontAwesomeIcon]="isFavoriteOrgsView ? 'search' : 'plus'"
          [link]="true"
          [langKey]="isFavoriteOrgsView ? 'common:textSearchOrganizations' : 'common:BtnAddOrganization'"
          (onClick)="isFavoriteOrgsView ? (isFavoriteOrgsView = false) : openAddOrgModal()">
        </yc-button>
      </div>
      <div *ngIf="!isFavoriteOrgsView && !showAddOrg"></div>

      <!-- Right Side -->
      <div class="d-flex align-items-center">
        <!-- Back -->
        <yc-button *ngIf="!isFavoriteOrgsView && (favoriteOrgs?.length > 0) && !useBucketSearch"
          class="me-3"
          [link]="true"
          [langKey]="'common:btnBack'"
          [defaultText]="'Back'"
          (onClick)="backToFavorites()">
        </yc-button>
  
        <!-- Cancel -->
        <yc-button
          class="me-3"
          [link]="true"
          [translatedText]="exitLinkText || ('common:btnCancel' | ycI18n: 'Cancel')"
          (onClick)="exitModal()">
        </yc-button>

        <!-- Select Org -->
        <yc-button *ngIf="!showNoResults && !!selectedOrg"
          class="me-3"
          [primary]="true"
          [langKey]="'search:textSelect'"
          [defaultText]="'Select'"
          (onClick)="orgSelected(selectedOrg, selectedOrg.isPrivateOrg, selectedOrg.isBucketOrg)">
        </yc-button>
      </div>
    </div>
  </div>
</yc-modal>


